/*  cart-drawer.js  – full file  */

class CartDrawer extends HTMLElement {
  constructor () {
    super();

    /* Cached selectors / flags */
    this.cartDrawerSidePanelBreakpoint = 1024;

    this.cartDrawerID = document
      .querySelector('.shopify-section-cart-drawer')
      ?.id?.replace('shopify-section-', '');

    this.headerID = document
      .querySelector('.shopify-section-header')
      ?.id?.replace('shopify-section-', '');

    // NEW → sticky/mobile header
    this.stickyHeaderID = document
      .querySelector('.shopify-section-header-sticky')
      ?.id?.replace('shopify-section-', '');

    this.cartRecommendationsID = document
      .querySelector('.shopify-section-cart-recommendations')
      ?.id?.replace('shopify-section-', '');

    this.CartDrawer = document.querySelector('cart-drawer');
    this.sidePanel = this.CartDrawer?.querySelector('.drawer-recommendations--side-panel');
    this.drawerRecommendations = this.CartDrawer?.querySelector('.drawer-recommendations');

    this.recommendationsBlockHasDifferentBg =
      this.dataset?.recommendationsBlockHasDifferentBg === 'true';
    this.enableShadow = this.dataset?.enableShadow === 'true';
    this.enableBorder = this.dataset?.enableBorder === 'true';

    this.cartLinks = document.querySelectorAll('#cart-link');
    this.drawer = this.CartDrawer?.querySelector('.drawer');
    this.overlay = this.CartDrawer?.querySelector('.overlay');

    /* Keyboard / overlay close */
    document.addEventListener('keyup', (e) => {
      if (e.code?.toUpperCase() === 'ESCAPE' && this.drawer.closest('.open')) this.close();
    });
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));

    this.setHeaderCartIconAccessibility();

    /* Theme-editor hooks */
    document.addEventListener(
      'shopify:section:load',
      (evt) => evt.target.closest('.shopify-section-cart-drawer') && this.open()
    );
    document.addEventListener(
      'shopify:section:select',
      (evt) => evt.target.closest('.shopify-section-cart-drawer') && this.sectionSelect()
    );
    document.addEventListener(
      'shopify:section:deselect',
      (evt) => evt.target.closest('.shopify-section-cart-drawer') && this.close()
    );

    /* Side-panel responsive tweaks */
    if (this.sidePanel) {
      this.adjustSidePanelForScreenSize();
      window.addEventListener('resize', this.adjustSidePanelForScreenSize.bind(this));
    }

    /* NEW → keep cart fresh on history navigation / BFCache */
    window.addEventListener('pageshow', () => this.sync());
    window.addEventListener('popstate', () => this.sync());
  }

  adjustSidePanelForScreenSize () {
    const header = this.drawerRecommendations?.querySelector('.js-cart-drawer-header');
    const tabs = this.drawerRecommendations?.querySelector('.tabs-block__results');
    const small = window.matchMedia(`(max-width:${this.cartDrawerSidePanelBreakpoint}px)`).matches;
    const toggle = (el, cls, on) => el && el.classList.toggle(cls, on);

    toggle(this.drawerRecommendations, 'drawer-recommendations--side-panel', !small);
    toggle(this.drawerRecommendations, 'scroll-area', !small);
    toggle(this.drawerRecommendations, 'hide-scrollbar', !small);
    toggle(this.drawerRecommendations, 'full-width-block', small && this.recommendationsBlockHasDifferentBg);
    toggle(this.drawerRecommendations, 'modal--shadow', !small && this.enableShadow);
    toggle(this.drawerRecommendations, 'modal--border', !small && this.enableBorder);
    toggle(header, 'cart-drawer__header', !small);
    toggle(tabs, 'tabs-block__results--allow-height-change', !small);
  }

  setHeaderCartIconAccessibility () {
    this.cartLinks = document.querySelectorAll('#cart-link');
    this.cartLinks.forEach((link) => {
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'dialog');
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(link);
      });
      link.addEventListener('keydown', (e) => {
        if (e.code.toUpperCase() === 'ENTER') {
          e.preventDefault();
          this.open(link);
        }
      });
    });
  }

  sectionSelect () {
    this.open(document.querySelector('#cart-link'));
  }

  open (triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    /* Close any open quick-view */
    if (document.body.classList.contains('quick-view-open')) {
      document.body.classList.remove('hidden', 'quick-view-open', 'quick-view-load');
      const qv = document.querySelector('.popup-wrapper__quick-view.open');
      if (qv) {
        qv.closest('details')?.removeAttribute('open');
        const wrap = qv.closest('.quick-view__content');
        wrap?.classList.remove('hide-cover');
        wrap?.querySelector('.overlay')?.classList.remove('open');
        qv.classList.remove('open');
        wrap.innerHTML = '';
      }
    }

    setTimeout(() => {
      this.classList.add('animate', 'open');
      this.drawer.classList.add('open');
      this.overlay.classList.add('open');
      document.body.classList.add('hidden');
      this.CartDrawer.setAttribute('tabindex', '-1');

      requestAnimationFrame(() => {
        setTimeout(() => {
          this.CartDrawer.querySelector('.button-close')?.focus();
          trapFocus(this.CartDrawer, this.CartDrawer.querySelector('.button-close'));
        }, 10);
      });
    }, 10);
  }

  close () {
    this.classList.remove('open');
    this.drawer.classList.remove('open');
    this.overlay.classList.remove('open');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('hidden');
    document.dispatchEvent(new CustomEvent('body:visible'));
  }

  renderContents (parsed) {
    if (!parsed?.sections) return;

    if (this.classList.contains('is-empty')) this.classList.remove('is-empty');

    /* Replace each section returned by Shopify */
    this.getSectionsToRender().forEach(({ id, selector }) => {
      const html = parsed.sections[id];
      if (!html) return;
      document.querySelectorAll(selector).forEach((el) => {
        el.innerHTML = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector(selector).innerHTML;
      });
    });

    /* Ensure any bubble duplicates all get the same HTML */
    this._syncAllBubbles(parsed.sections);

    /* Re-open if theme marks “open-after-adding” */
    if (this.classList.contains('open-after-adding')) {
      setTimeout(() => {
        this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
        this.open();
      });
    }
  }

  getSectionsToRender () {
    const arr = [
      { id: this.cartDrawerID, selector: '#CartDrawer' },
      { id: this.headerID, selector: `#cart-icon-bubble-${this.headerID}` }
    ];
    if (this.stickyHeaderID) {
      arr.push({ id: this.stickyHeaderID, selector: `#cart-icon-bubble-${this.stickyHeaderID}` });
    }
    if (this.cartRecommendationsID) {
      arr.push({ id: this.cartRecommendationsID, selector: '.tabs-block__results' });
    }
    return arr;
  }

  setActiveElement (el) {
    this.activeElement = el;
    if (el.closest('.cart-icon') && el.querySelector('a.cart'))
      this.activeElement = el.querySelector('a.cart');
    if (el.closest('.popup-wrapper__quick-view') && el.closest('.card-product'))
      this.activeElement = el.closest('.card-product').querySelector('.card-quick-view');
  }

  _syncAllBubbles (sections) {
    const html = sections[this.headerID] || sections[this.stickyHeaderID];
    if (!html) return;
    const inner = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('[id^="cart-icon-bubble-"]')?.innerHTML;
    if (!inner) return;
    document.querySelectorAll('[id^="cart-icon-bubble-"]').forEach((bub) => {
      bub.innerHTML = inner;
    });
  }

  sync () {
    if (!this.cartDrawerID) return;

    const sectionList = [
      this.cartDrawerID,
      this.headerID,
      this.stickyHeaderID,
      this.cartRecommendationsID
    ]
      .filter(Boolean)
      .join(',');

    fetch(`/?sections=${sectionList}&sections_url=${window.location.pathname}`)
      .then((r) => r.json())
      .then((html) => {
        const p = new DOMParser();

        if (html[this.cartDrawerID]) {
          const inner = p
            .parseFromString(html[this.cartDrawerID], 'text/html')
            .querySelector('#CartDrawer')?.innerHTML;
          if (inner) this.querySelector('#CartDrawer').innerHTML = inner;
        }

        if (this.cartRecommendationsID && html[this.cartRecommendationsID]) {
          const inner = p
            .parseFromString(html[this.cartRecommendationsID], 'text/html')
            .querySelector('.tabs-block__results')?.innerHTML;
          if (inner) this.drawerRecommendations.innerHTML = inner;
        }

        /* Bubbles */
        this._syncAllBubbles(html);
      })
      .catch((err) => console.error('Cart sync error:', err));
  }
}

/* Register custom element */
customElements.define('cart-drawer', CartDrawer);

/* One initial sync on first load */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelector('cart-drawer')?.sync();
});
